/**
 * Click-to-play video facades.
 *
 * functions/video-facade.php renders `[data-video-facade]` wrappers holding a
 * poster button and the original provider iframe in a <template>. Clicking the
 * button swaps the iframe in with autoplay on, so the player's scripts and
 * cookies only load for visitors who actually watch.
 */
class VideoFacade {
  /**
   * @param {HTMLElement} el The facade wrapper.
   */
  constructor(el) {
    this.el = el;
    this.button = el.querySelector(".video-facade__button");
    this.template = el.querySelector("template[data-video-facade-embed]");
    this.play = this.play.bind(this);
  }

  /**
   * Attach the click handler.
   *
   * @returns {void}
   */
  init() {
    if (!this.button || !this.template) return;
    this.button.addEventListener("click", this.play, { once: true });
  }

  /**
   * Replace the poster with the provider iframe and start playback.
   *
   * @returns {void}
   */
  play() {
    const embed = this.template.content.cloneNode(true);
    const iframe = embed.querySelector("iframe");

    if (iframe) {
      const url = new URL(iframe.src);
      url.searchParams.set("autoplay", "1");
      iframe.src = url.toString();

      const allow = (iframe.getAttribute("allow") || "").split(";").map((s) => s.trim()).filter(Boolean);
      if (!allow.includes("autoplay")) allow.push("autoplay");
      iframe.setAttribute("allow", allow.join("; "));
    }

    this.el.classList.add("video-facade--playing");
    this.el.replaceChildren(embed);
    iframe?.focus();
  }
}

/**
 * Initialise every video facade on the page.
 *
 * @returns {void}
 */
export default () => {
  document.querySelectorAll("[data-video-facade]").forEach((el) => new VideoFacade(el).init());
};
